import type { AnalysisBaseSchema } from '@libs/data-access/entities'
import {
  AnalyticsBoardGraphLineIcon,
  Flex,
  IconWrap,
  Inline,
  SegmentedControl,
  Text,
  Title
} from '@libs/ui/ds'
import { usePlanningAnalysisTranslation } from '@libs/utils/translations'
import { useMemoSelector } from '@spa/redux/hooks'
import { getParams } from '@spa/scenes/Plannings/redux/selectors'
import React from 'react'
import type { FC } from 'react'
import { getAccountCurrencyIsoCode } from 'snap-redux/modules/accounts'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { getLocationsByAccountId } from 'snap-redux/modules/locations/selectors'

import { getStatistics } from './utils/getStatistics'

type Props = {
  real: boolean
  toggleReal: () => void
  canSeeWagesRatio: boolean
  statistics: AnalysisBaseSchema
}
const WeeklySummary: FC<Props> = ({
  real,
  canSeeWagesRatio = false,
  toggleReal,
  statistics
}) => {
  const { t, tDates } = usePlanningAnalysisTranslation()
  const account = useMemoSelector(getCurrentAccount)
  const { locationId } = useMemoSelector(getParams)
  const locations = useMemoSelector(state =>
    getLocationsByAccountId(state, account.id)
  )

  const {
    payroll_in_euro,
    payroll_ratio,
    productivity_in_euro_per_hour,
    sales_in_euro,
    worked_in_minutes,
    overtime_duration_in_minutes,
    overtime_cost_in_euro
  } = getStatistics(real, statistics)

  const location = locations.get(locationId)
  const payroll_ratio_objective = location?.wage_ratio_objective ?? 30
  const account_currency = getAccountCurrencyIsoCode(account)

  const switchData = [
    { label: t('weeklyRecap.switchButton.budget'), value: 'budget' },
    { label: t('weeklyRecap.switchButton.real'), value: 'real' }
  ]

  return (
    <Inline stretch='end' className='relative m-2.5 w-full flex-nowrap gap-4 '>
      <Flex className='w-full shrink basis-0 flex-nowrap items-center justify-center rounded-lg bg-neutral-100 px-4 py-2'>
        <Flex row className='mb-1 items-center justify-center gap-2'>
          <IconWrap icon={<AnalyticsBoardGraphLineIcon />} />
          <Title size={4}>{t('weeklyRecap.title')}</Title>
        </Flex>
        <SegmentedControl
          data={switchData}
          onChange={toggleReal}
          fillWidth
          defaultValue={real ? 'real' : 'budget'}
          className='w-full'
        />
      </Flex>
      <Flex
        row
        className='shrink basis-0 flex-nowrap items-center justify-between gap-2 rounded-lg bg-neutral-100 px-4 py-2 text-center'
      >
        <Title size={5}>{t('weeklyRecap.weekRatio')}</Title>
        <Flex className='shrink grow basis-0 items-center'>
          <Title size={5}>{t('weeklyRecap.sales')}</Title>
          <Title size={5} className='text-semantic-blue-400'>
            {sales_in_euro} {account_currency}
          </Title>
        </Flex>
        {canSeeWagesRatio && (
          <Flex className='shrink grow basis-0 items-center'>
            <Title size={5}>{t('weeklyRecap.payroll')}</Title>
            <Title size={5} className='text-semantic-blue-400'>
              {payroll_in_euro} {account_currency}
            </Title>
          </Flex>
        )}
        <Flex className='shrink grow basis-0 items-center'>
          <Title size={5}>
            {real
              ? t('weeklyRecap.workedHours')
              : t('weeklyRecap.plannedHours')}
          </Title>
          <Title size={5} className='text-semantic-blue-400'>
            {tDates('duration.minuteDecimalToHours', {
              duration: worked_in_minutes
            })}
          </Title>
        </Flex>
        <Flex className='shrink grow basis-0 items-center'>
          <Title size={5}>{t('weeklyRecap.productivity')}</Title>
          <Text size='information' className='mb-0.5'>
            {t('weeklyRecap.salesPerHours')}
          </Text>
          <Title size={5}>
            {productivity_in_euro_per_hour} {account_currency}/h
          </Title>
        </Flex>
        {canSeeWagesRatio && (
          <Flex className='shrink grow basis-0 items-center'>
            <Title size={5}>{t('weeklyRecap.productivityRatio')}</Title>
            <Title
              size={5}
              className={`${payroll_ratio > 0 && 'text-semantic-green-500'} ${
                payroll_ratio > payroll_ratio_objective &&
                'text-semantic-orange-500'
              }`}
            >
              {payroll_ratio}%
            </Title>
          </Flex>
        )}
        <Flex className='shrink grow basis-0 items-center'>
          <Title size={5}>{t('weeklyRecap.extraHours')}</Title>
          <Text size='information' className='mb-0.5'>
            {t('weeklyRecap.outOfContracts')}
          </Text>
          <Flex row className='shrink grow basis-0 items-center'>
            <Title size={5}>
              {tDates('duration.minuteDecimalToHours', {
                duration: overtime_duration_in_minutes
              })}
            </Title>
            <Title size={5} className='ml-1 text-semantic-red-400'>
              {(overtime_cost_in_euro || 0).toFixed(2)} {account_currency}
            </Title>
          </Flex>
        </Flex>
      </Flex>
    </Inline>
  )
}

export default WeeklySummary
